import { useEffect, useState, useMemo } from 'react';
import { usePrevious } from "./usePrevious";
import { useSelector } from 'react-redux';
import { fetchReferenceDataFile } from "../clientRequests";
import { referenceDataFileListSelector } from "../../store/selectors/referenceData";
import { clientEntriesSelector } from "../../store/selectors/clientEntries";
import { matchById, matchByGuid, clampValue, compose, I, isEmpty } from "../utils";
import { referenceDataEndpointSelector, clientTypeSelector } from "../../store/selectors/clientSettings";
import { useReferenceDataInDispatchSelector } from "../../store/selectors/entities/form";

// currently just an in memory cache, can be swapped out for another solution
const refCache = {};

// TODO: making these structured remote data types could be useful down the road
const NOTASKED = 'notasked';
const LOADING = 'loading';
const SUCCESS = 'success';
const FAILURE = 'failure';

// given a reference data file id, returns an object
// with rows as an empty array until the data is loaded
export const useReferenceData = refFileId => {
  const refDataFiles = useSelector(referenceDataFileListSelector) || [];
  const refDataEndpoint = useSelector(referenceDataEndpointSelector);
  const metaData = refDataFiles.find(matchById(refFileId)) || {}; // getting meta data
  const {
    headers = [],
    rows = [],
    request: existingRequest
  } = refCache[refFileId] || {};
  const [fileState, updateFileState] = useState(() => {
    if (rows.length) {
      return SUCCESS;
    }
    if (existingRequest) {
      return LOADING;
    }
    return NOTASKED;
  });
  if (refFileId && fileState === NOTASKED) {
    let request = existingRequest ?? fetchReferenceDataFile({
      endpoint: refDataEndpoint,
      refFileId
    });
    if (!existingRequest && refDataEndpoint.includes('column_caches.json')) {
      request = request.then(response => {
        if (response && Array.isArray(response.columns)) {
          const columns = response.columns;
          const headers = metaData.headers;

          // Get the number of rows from the first column's length
          const rowCount = columns[0]?.length || 0;

          // Create rows directly using mapping
          const rows = Array(rowCount).fill().map((_, rowIndex) => columns.map(column => column[rowIndex] || ''));
          return {
            metaData,
            headers,
            rows,
            id: refFileId
          };
        }
        return response;
      });
    }
    refCache[refFileId] = {
      rows,
      headers,
      request
    };
    updateFileState(LOADING);
  }

  // handling requests
  useEffect(() => {
    let cancelRequest = false;

    // do nothing if no file id, or we already have columns
    if (!refFileId || fileState === SUCCESS) {
      return;
    }
    const handleError = () => {
      if (cancelRequest) {
        return;
      }
      updateFileState(FAILURE);
    };
    const addData = ({
      rows = [],
      headers = []
    }) => {
      if (cancelRequest) {
        return;
      }
      refCache[refFileId] = {
        rows,
        headers,
        request: null
      };
      updateFileState(SUCCESS);
      return rows;
    };
    const currentRequest = existingRequest ? existingRequest : refCache[refFileId]?.request || Promise.reject('canceled');
    currentRequest.then(addData).catch(handleError);
    return function cleanup() {
      cancelRequest = true;
    };
  }, [refFileId]);
  return {
    ...metaData,
    fileState,
    headers,
    rows
  };
};

// returns an empty array or an array of strings
// used to retrieve a single column of data
export const useReferenceDataColumn = ({
  reference_data_id,
  reference_data_column,
  default_options = []
}) => {
  const referenceData = useReferenceData(reference_data_id);
  const {
    headers = [],
    rows
  } = referenceData;
  if (!reference_data_id || !reference_data_column) {
    return {
      ...referenceData,
      column: default_options
    };
  }
  if (!rows || !rows.length) {
    return {
      ...referenceData,
      column: []
    };
  }
  const columnIndex = headers.indexOf(reference_data_column);
  return {
    ...referenceData,
    name: reference_data_column,
    columnIndex,
    rows
  };
};

// returns an array of options objects (common props like label/text/etc.)
// representing a single column from each row
export const useReferenceDataOptions = ({
  reference_data_id,
  reference_data_column,
  default_options = [],
  removeDuplicates = false,
  currentlySelectedOptions = []
}) => {
  const referenceData = useReferenceDataColumn({
    reference_data_id,
    reference_data_column,
    default_options
  });

  // we shouldn't alter the default options, so we just return them
  if (!reference_data_column) {
    return {
      ...referenceData,
      options: default_options
    };
  }
  const {
    columnIndex,
    rows
  } = referenceData;

  // Unique the options & remove selected options if removeDuplicates is true
  const {
    options
  } = rows.reduce((acc, row, i) => {
    const {
      seen,
      options
    } = acc;
    const text = row[columnIndex];
    if (!text) {
      return acc;
    }
    if (!currentlySelectedOptions.includes(text) && (!removeDuplicates || !seen.has(text))) {
      seen.add(text);
      options.push({
        text,
        value: text,
        position: options.length,
        //options position
        refRowIndex: i,
        // true row index
        label: text
      });
    }
    return acc;
  }, {
    seen: new Set(),
    options: []
  });
  return {
    ...referenceData,
    options
  };
};
const lookupReferenceValue = (referenceFieldValue, isSameFile, sourceReferenceColumn, rows, headers,
// array
reference_data_column,
// string
refRowIndex,
// number
type, minimum, maximum) => {
  const emptyRefValue = isEmpty(referenceFieldValue);
  if (!headers || !rows.length || emptyRefValue || !reference_data_column) {
    return false;
  }
  const columnIndex = headers.indexOf(reference_data_column);
  let value;
  if (isSameFile && ![null, undefined, -1].includes(refRowIndex)) {
    // easy lookup if the file is the same
    value = rows[refRowIndex]?.[columnIndex] || '';
  } else {
    // but if the file is different, or there's no index,
    // then we need to look up the first occurance of the value in the
    // dependent column of THIS entry's file
    const sourceColumnIndex = headers.indexOf(sourceReferenceColumn);
    if (sourceColumnIndex === -1) {
      return false;
    }
    const rowIndex = rows.findIndex(row => row[sourceColumnIndex] === referenceFieldValue);
    value = rows[rowIndex]?.[columnIndex] || '';
  }
  return clampValue({
    value,
    type,
    maximum,
    minimum
  });
};

// HOC version to wrap field components
// potentially intercepting or updating their values
// used with non-dropdown fields
export const withDependentReferenceData = Component => props => {
  const {
    dependent_entry,
    reference_data_id,
    reference_data_column,
    value,
    update,
    type,
    minimum,
    maximum
  } = props || {};
  const {
    guid = null
  } = dependent_entry || {};

  // get the dependent entry's value
  const {
    value: referenceFieldValue,
    refRowIndex,
    reference_data_id: sourceReferenceFileId,
    reference_data_column: sourceReferenceColumn
  } = useSelector(clientEntriesSelector).find(matchByGuid(guid)) || {};
  const clientType = useSelector(clientTypeSelector);
  const useReferenceDataInDispatch = useSelector(useReferenceDataInDispatchSelector);
  const {
    rows,
    headers,
    id: refFileId
  } = useReferenceData(reference_data_id);
  const [edited, setEditedState] = useState(false);
  const prevRefValue = usePrevious(referenceFieldValue);
  const prevRefIndex = usePrevious(refRowIndex);
  const isSameFile = refFileId === sourceReferenceFileId;
  const memoProps = [referenceFieldValue, isSameFile, sourceReferenceColumn, rows, headers, reference_data_column, refRowIndex, type, minimum, maximum];
  const dependentValue = useMemo(() => lookupReferenceValue(...memoProps), memoProps);

  // update the allowed value initially or if the parent column value changes
  useEffect(() => {
    const sourceValChanged = prevRefValue !== undefined && prevRefValue !== referenceFieldValue;
    const sourceRefIndexChanged = prevRefIndex !== undefined && prevRefIndex !== refRowIndex;
    const hasWrongValue = dependentValue && value !== dependentValue && !edited;
    // no op until these are available
    if (headers && rows.length && referenceFieldValue && dependentValue !== false) {
      if (!sourceValChanged && !sourceRefIndexChanged && value && value !== dependentValue) {
        // If the value is updated manually by the user set edited to true to prevent the value from being overwritten
        setEditedState(true);
      } else if (sourceValChanged || sourceRefIndexChanged || hasWrongValue) {
        // Source value changed or the field has the wrong value, update the value to be consistent with the source value
        update(dependentValue, {
          // We pass in an object to the 2nd param containing the default attribute if the field is a Link,
          // because the LinkPreview component requires the url to be stored in the default property
          ...(type === 'Link' && {
            default: dependentValue
          }),
          refRowIndex
        });
        setEditedState(false);
      }
    }
  }, [reference_data_column, referenceFieldValue, refRowIndex, rows]);

  // If we are in the Dispatch Client, we want the user to be able to input a value into a text field
  // even if it's a dependent reference data field and the user selected NO to the "Would you like to
  // use Reference Data popup"
  const overrideLoadingText = clientType === 'dispatch' && !useReferenceDataInDispatch;
  const extraProps = dependent_entry && !overrideLoadingText && (!headers || !rows.length) ? {
    readOnly: true,
    errors: '',
    value: 'Loading...'
  } : {};
  const overrideProps = {
    ...props,
    ...extraProps
  };
  return <Component {...overrideProps} />;
};

//Returns an array containing the filtered options and the dependent value used for reference data.
const lookupReferenceOptions = (referenceFieldValue, isSameFile, sourceReferenceColumn, rows, headers, reference_data_column, refRowIndex) => {
  const emptyRefValue = isEmpty(referenceFieldValue);
  if (!headers || !rows.length || emptyRefValue || !reference_data_column) {
    return false;
  }
  const sourceColumnIndex = headers.indexOf(sourceReferenceColumn);
  const dependentColumnIndex = headers.indexOf(reference_data_column);
  let dependentValue = false;
  let newRefIndex;
  const dependentOptions = sourceColumnIndex < 0 ? [] : rows.reduce(
  // Get the index of every occurence of referenceFieldValue (no matter the size) and return a new array with all those indexes
  (acc, row, index) => {
    const matchValue = row[sourceColumnIndex];
    if (matchValue === referenceFieldValue || emptyRefValue) {
      const value = row[dependentColumnIndex];
      acc.push({
        value,
        label: value,
        refRowIndex: index,
        position: acc.length //options position
      });
      // pick the first value by default, the matching row if same file
      if (!emptyRefValue && (acc.length == 1 || isSameFile && acc.length - 1 === refRowIndex)) {
        dependentValue = value;
        newRefIndex = index;
      }
    }
    return acc;
  }, []);

  // Filter the dependentOptions by the unique key
  // TODO: NOT SURE THIS IS NEEDED, but second one makes more sense
  // const dependentFilterOptions = dependentOptions.filter(
  //   (v, i, a) => a.findIndex(v2 => ['value'].every(k => v2[k] === v[k])) === i,
  //   []
  // )
  // const dependentFilterOptions = dependentOptions.filter(
  //   (v, i, a) => a.findIndex(v2 => v2.value === v.value) === i
  // )
  return {
    dependentOptions,
    dependentValue,
    newRefIndex
  };
};

// Filter Single Choice Dropdown options based on the dependent value instead of returning only one option in the dropdowns.
// We remove the selectedOption value from props as it is not necessary as we're setting a specific value -- in some situations having this value can lead to the wrong option being selected.
export const withDependentReferenceDataOptions = Component => ({
  selectedOption,
  ...props
}) => {
  const {
    dependent_entry,
    reference_data_id,
    reference_data_column,
    update,
    default: defaultValue,
    style,
    value
  } = props || {};
  const clientType = useSelector(clientTypeSelector);
  const useReferenceDataInDispatch = useSelector(useReferenceDataInDispatchSelector);
  const {
    guid = null
  } = dependent_entry || {};
  const isDependentField = dependent_entry && guid;
  const allowsDuplicates = [0, 4].includes(parseInt(style, 10));
  const {
    rows,
    headers,
    id: refFileId
  } = useReferenceData(reference_data_id);
  useEffect(() => {
    if (!value && defaultValue === '' && rows.length) {
      update(rows[0][headers.findIndex(header => header === reference_data_column)], {
        refRowIndex: allowsDuplicates ? 0 : undefined
      });
    }
  }, [value, defaultValue, rows]);
  const overrideLoadingText = clientType === 'dispatch' && !useReferenceDataInDispatch;
  const extraProps = isDependentField && !overrideLoadingText && reference_data_column && !rows.length ? {
    disabled: true,
    errors: '',
    placeholder: 'Loading...'
  } : {};

  // get the dependent entry's value & props
  const {
    value: referenceFieldValue,
    refRowIndex,
    reference_data_id: sourceReferenceFileId,
    reference_data_column: sourceReferenceColumn
  } = useSelector(clientEntriesSelector).find(matchByGuid(guid)) || {};
  const isSameFile = refFileId === sourceReferenceFileId; // true if the file is the same
  const prevRefValue = usePrevious(referenceFieldValue);
  const prevRefIndex = usePrevious(refRowIndex);
  const memoProps = [referenceFieldValue, isSameFile,
  // isSameFile
  sourceReferenceColumn, rows, headers, reference_data_column, refRowIndex];

  // lookups could get reaaaaally expensive
  const {
    dependentOptions = [],
    dependentValue,
    newRefIndex
  } = useMemo(() => lookupReferenceOptions(...memoProps), memoProps);
  useEffect(() => {
    if (isDependentField) {
      const sourceValChange = prevRefValue !== referenceFieldValue;
      const sourceRefIndexChanged = prevRefIndex !== undefined && prevRefIndex !== refRowIndex;
      if (headers && rows.length && referenceFieldValue && dependentValue !== false) {
        if (sourceValChange || sourceRefIndexChanged) {
          // Source value changed or the field has the wrong value
          // update the value to be consistent with the source's value
          update(dependentValue, {
            refRowIndex: allowsDuplicates ? newRefIndex : undefined
          });
        }
      }
    }
  }, [reference_data_column, prevRefValue, referenceFieldValue, rows, refRowIndex, isDependentField]);
  const overrideProps = {
    ...props,
    ...extraProps
  };
  if (dependentOptions.length) {
    overrideProps.options = dependentOptions;
  }
  return <Component {...overrideProps} />;
};

// for components that use something other than value as their value
export const _withDependentReferenceDataCustomField = (valueName, validateValue = I) => Component => props => {
  const {
    value,
    ...otherProps
  } = props;
  const altProps = validateValue(value) ? {
    [valueName]: value
  } : {};
  return <Component {...otherProps} {...altProps} />;
};
export const withDependentReferenceDataCustomField = (valueName, validateValue) => compose(_withDependentReferenceDataCustomField(valueName, validateValue), withDependentReferenceData);